<template>
  <VueFinalModal
    v-model="isOpen"
    content-transition="vfm-fade"
    :click-to-close="true"
    class="flex justify-center items-center p-6"
    content-class="relative w-full max-w-lg bg-white rounded-[32px] shadow-2xl overflow-hidden z-50 ring-1 ring-black/5"
    @close="close"
  >
    <div class="p-6 border-b border-[#1c4848]/10 flex items-center justify-between">
      <div>
        <p class="text-[10px] font-bold uppercase tracking-widest text-[#1c4848]/50">
          {{ t('home.location.modalBadge') }}
        </p>
        <h3 class="text-lg font-bold text-[#1c4848]">{{ t('home.location.modalTitle') }}</h3>
      </div>
      <button type="button" class="p-2 rounded-xl hover:bg-[#1c4848]/5" @click="close">
        <iconify-icon icon="solar:close-circle-linear" class="text-xl text-[#1c4848]/60"></iconify-icon>
      </button>
    </div>

    <div class="p-6 space-y-4">
      <div class="flex gap-2 bg-[#fafbfc] border border-[#1c4848]/10 rounded-2xl p-1">
        <button
          type="button"
          class="flex-1 px-3 py-2 rounded-xl text-xs font-bold uppercase tracking-widest transition-colors"
          :class="tab === 'cities' ? 'bg-white text-[#1c4848] shadow-sm' : 'text-[#1c4848]/50 hover:text-[#1c4848]'"
          @click="tab = 'cities'"
        >
          {{ t('home.location.tabs.cities') }}
        </button>
        <button
          type="button"
          class="flex-1 px-3 py-2 rounded-xl text-xs font-bold uppercase tracking-widest transition-colors"
          :class="tab === 'coords' ? 'bg-white text-[#1c4848] shadow-sm' : 'text-[#1c4848]/50 hover:text-[#1c4848]'"
          @click="tab = 'coords'"
        >
          {{ t('home.location.tabs.coords') }}
        </button>
      </div>

      <div v-if="tab === 'cities'" class="flex gap-2">
        <div class="flex-1 relative">
          <iconify-icon
            icon="solar:magnifer-linear"
            class="absolute left-3 top-1/2 -translate-y-1/2 text-[#1c4848]/40"
          ></iconify-icon>
          <input
            v-model="search"
            type="text"
            :placeholder="t('home.location.searchPlaceholder')"
            class="w-full pl-10 pr-3 py-2.5 rounded-2xl border border-[#1c4848]/10 focus:outline-none focus:ring-4 focus:ring-[#1c4848]/5"
          />
        </div>
        <button
          type="button"
          class="px-4 py-2.5 rounded-2xl bg-[#1c4848] text-white text-sm font-semibold shadow-lg hover:bg-[#153838] active:scale-95 transition-all"
          @click="reload"
          :disabled="isLoading"
        >
          {{ t('home.location.refresh') }}
        </button>
      </div>

      <div v-if="loadError" class="text-sm text-rose-600 bg-rose-50 border border-rose-100 rounded-2xl p-3">
        {{ t('home.location.loadError') }}
      </div>

      <div v-if="tab === 'cities'">
        <div
          ref="scrollEl"
          class="max-h-[360px] overflow-y-auto space-y-2 pr-1"
          @scroll="onScroll"
        >
          <button
            v-for="c in citiesList"
            :key="c.id"
            type="button"
            class="w-full text-left px-4 py-3 rounded-2xl border border-[#1c4848]/10 hover:bg-[#fafbfc] transition-colors flex items-center justify-between"
            @click="select(c)"
          >
            <div class="min-w-0">
              <p class="text-sm font-semibold text-[#1c4848] truncate">{{ c.name }}</p>
              <p class="text-[10px] font-mono text-[#1c4848]/40">city_id: {{ c.id }}</p>
            </div>
            <iconify-icon icon="solar:arrow-right-up-linear" class="text-lg text-[#1c4848]/40"></iconify-icon>
          </button>

          <div v-if="!isLoading && citiesList.length === 0" class="text-sm text-[#1c4848]/50 text-center py-10">
            {{ t('home.location.empty') }}
          </div>

          <div v-if="isLoading" class="text-sm text-[#1c4848]/50 text-center py-10">
            {{ t('home.location.loading') }}
          </div>

          <div v-else-if="hasMore" class="text-[11px] text-[#1c4848]/40 text-center py-4">
            {{ t('home.location.scrollMore') }}
          </div>
        </div>
      </div>

      <div v-else class="space-y-4">
        <button
          type="button"
          class="w-full px-4 py-3 rounded-2xl bg-[#1c4848]/5 text-[#1c4848] text-sm font-semibold hover:bg-[#1c4848]/10 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="geoBusy"
          @click="$emit('use-my-location')"
        >
          <iconify-icon icon="solar:crosshair-linear" class="text-lg"></iconify-icon>
          {{ t('home.location.useMyLocation') }}
        </button>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label class="block text-[10px] font-bold text-[#1c4848]/60 uppercase tracking-wider mb-1.5 ml-1">
              {{ t('home.location.coords.latitude') }}
            </label>
            <input
              v-model="lat"
              inputmode="decimal"
              type="text"
              :placeholder="t('home.location.coords.latPlaceholder')"
              class="w-full px-4 py-2.5 rounded-2xl border border-[#1c4848]/10 focus:outline-none focus:ring-4 focus:ring-[#1c4848]/5"
            />
          </div>
          <div>
            <label class="block text-[10px] font-bold text-[#1c4848]/60 uppercase tracking-wider mb-1.5 ml-1">
              {{ t('home.location.coords.longitude') }}
            </label>
            <input
              v-model="lng"
              inputmode="decimal"
              type="text"
              :placeholder="t('home.location.coords.lngPlaceholder')"
              class="w-full px-4 py-2.5 rounded-2xl border border-[#1c4848]/10 focus:outline-none focus:ring-4 focus:ring-[#1c4848]/5"
            />
          </div>
        </div>

        <div>
          <label class="block text-[10px] font-bold text-[#1c4848]/60 uppercase tracking-wider mb-1.5 ml-1">
            {{ t('home.location.coords.timezone') }}
          </label>
          <input
            v-model="tz"
            type="text"
            :placeholder="t('home.location.coords.tzPlaceholder')"
            class="w-full px-4 py-2.5 rounded-2xl border border-[#1c4848]/10 focus:outline-none focus:ring-4 focus:ring-[#1c4848]/5"
          />
          <p class="text-[11px] text-[#1c4848]/40 mt-1 ml-1">
            {{ t('home.location.coords.tzHint') }}
          </p>
        </div>

        <div v-if="coordsError" class="text-sm text-rose-600 bg-rose-50 border border-rose-100 rounded-2xl p-3">
          {{ coordsError }}
        </div>

        <div class="flex gap-2">
          <button
            type="button"
            class="flex-1 px-4 py-3 rounded-2xl bg-[#1c4848] text-white text-sm font-semibold shadow-lg hover:bg-[#153838] active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="!canApplyCoords"
            @click="applyCoords"
          >
            {{ t('home.location.coords.apply') }}
          </button>
          <button
            type="button"
            class="px-4 py-3 rounded-2xl border border-[#1c4848]/10 text-sm font-semibold text-[#1c4848]/70 hover:text-[#1c4848] hover:bg-[#fafbfc] transition-colors"
            @click="resetCoords"
          >
            {{ t('home.location.coords.clear') }}
          </button>
        </div>
      </div>
    </div>
  </VueFinalModal>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { VueFinalModal } from 'vue-final-modal';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  cities: { type: Array, default: () => [] },
  isLoading: { type: Boolean, default: false },
  loadError: { type: Boolean, default: false },
  hasMore: { type: Boolean, default: false },
  searchQuery: { type: String, default: '' },
  geoBusy: { type: Boolean, default: false },
});
const emit = defineEmits([
  'update:modelValue',
  'select',
  'select-coords',
  'use-my-location',
  'reload',
  'update:searchQuery',
  'load-more',
]);

const scrollEl = ref(null);
const tab = ref('cities'); // cities | coords
const lat = ref('');
const lng = ref('');
const tz = ref('');
const coordsError = ref('');

const isOpen = computed({
  get: () => props.modelValue,
  set: (v) => emit('update:modelValue', v),
});

watch(
  () => isOpen.value,
  (open) => {
    if (open) {
      tab.value = 'cities';
      resetCoords();
      emit('update:searchQuery', '');
      requestAnimationFrame(() => {
        if (scrollEl.value) scrollEl.value.scrollTop = 0;
      });
    }
  }
);

const citiesList = computed(() => (Array.isArray(props.cities) ? props.cities : []));

const search = computed({
  get: () => props.searchQuery,
  set: (v) => emit('update:searchQuery', v),
});

function close() {
  isOpen.value = false;
}

function select(city) {
  emit('select', city);
  close();
}

function reload() {
  emit('reload');
}

function onScroll() {
  const el = scrollEl.value;
  if (!el || props.isLoading || !props.hasMore) return;
  const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 120;
  if (nearBottom) emit('load-more');
}

const canApplyCoords = computed(() => {
  const la = Number(String(lat.value).trim());
  const lo = Number(String(lng.value).trim());
  return Number.isFinite(la) && Number.isFinite(lo);
});

function resetCoords() {
  lat.value = '';
  lng.value = '';
  tz.value = '';
  coordsError.value = '';
}

function applyCoords() {
  coordsError.value = '';
  const la = Number(String(lat.value).trim());
  const lo = Number(String(lng.value).trim());
  if (!Number.isFinite(la) || la < -90 || la > 90) {
    coordsError.value = t('home.location.coords.invalidLat');
    return;
  }
  if (!Number.isFinite(lo) || lo < -180 || lo > 180) {
    coordsError.value = t('home.location.coords.invalidLng');
    return;
  }
  emit('select-coords', { latitude: la, longitude: lo, timezone: String(tz.value || '').trim() || null });
  close();
}
</script>

